import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Form,
    Button,
    Input,
    TextArea,
    Checkbox,
    Dropdown,
    Segment,
    Header,
    Icon,
    Message,
} from 'semantic-ui-react';
import {
    eventService,
    EventDate,
    PostType,
} from '../../../services/eventService';
import { axios } from '../../../services/axiosService';
import { DuplicateModal } from '../list/DuplicateModal';
import { DedupAuditPanel } from './DedupAuditPanel';
import styles from './EventForm.module.scss';

interface EventFormProps {
    postType: PostType;
    initialData?: any;
    onSubmit: (data: any) => Promise<void>;
    onCancel: () => void;
    isEdit?: boolean;
}

interface EventDateForm extends EventDate {
    id?: string;
}

interface Language {
    i18nCode: string;
    nativeName: string;
    enName: string;
}

interface TranslationData {
    title: string;
    summary: string;
    content: string;
}

export const EventForm: React.FC<EventFormProps> = ({
    postType,
    initialData,
    onSubmit,
    onCancel,
    isEdit = false,
}) => {
    const isEvent = postType === 'event';
    const contentTypeLabel = postType === 'event' ? 'Event' : 'Article';
    const [activeLanguageTab, setActiveLanguageTab] = useState<string>('en');
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState<any[]>([]);
    const [languages, setLanguages] = useState<Language[]>([]);

    // Duplicate management state
    const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
    const [isDuplicate, setIsDuplicate] = useState(initialData?.isDuplicate || false);
    const [duplicateOf, setDuplicateOf] = useState<any>(initialData?.duplicateOf || null);
    const [duplicateLoading, setDuplicateLoading] = useState(false);

    // English fields
    const [title, setTitle] = useState(initialData?.title || '');
    const [summary, setSummary] = useState(initialData?.summary || '');
    const [content, setContent] = useState(initialData?.content || '');
    const [eventLocation, setEventLocation] = useState(
        initialData?.eventLocation || '',
    );
    const [keywords, setKeywords] = useState(
        initialData?.keywords?.join(', ') || '',
    );
    const [categoryId, setCategoryId] = useState(
        initialData?.category?.id || '',
    );
    const [publishedAt, setPublishedAt] = useState(
        initialData?.publishedAt
            ? new Date(initialData.publishedAt).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
    );
    const [isUrgent, setIsUrgent] = useState(initialData?.isUrgent || false);
    const [isActive, setIsActive] = useState(
        initialData?.isActive !== undefined ? initialData.isActive : true,
    );
    const [sourceUrl, setSourceUrl] = useState(initialData?.sourceUrl || '');
    const [imageUrl, setImageUrl] = useState(initialData?.imageUrl || '');

    // Event dates
    const [eventDates, setEventDates] = useState<EventDateForm[]>(
        initialData?.eventDates?.map((ed: any, index: number) => ({
            eventDate: ed.eventDate,
            startTime: ed.startTime || '',
            endTime: ed.endTime || '',
            notes: ed.notes || '',
            sortOrder: index,
        })) || [
            {
                eventDate: '',
                startTime: '',
                endTime: '',
                notes: '',
                sortOrder: 0,
            },
        ],
    );

    // Dynamic translations state
    const [translations, setTranslations] = useState<
        Record<string, TranslationData>
    >({});

    useEffect(() => {
        loadCategories();
        loadLanguages();
    }, []);

    // Initialize translations when languages are loaded
    useEffect(() => {
        if (languages.length > 0) {
            const initialTranslations: Record<string, TranslationData> = {};

            // Convert translations array from API to Record format
            const translationsMap: Record<string, any> = {};
            if (initialData?.translations && Array.isArray(initialData.translations)) {
                initialData.translations.forEach((t: any) => {
                    translationsMap[t.languageCode] = t;
                });
            }

            languages.forEach((lang) => {
                if (lang.i18nCode !== 'en') {
                    const translation = translationsMap[lang.i18nCode];
                    initialTranslations[lang.i18nCode] = {
                        title: translation?.title || '',
                        summary: translation?.summary || '',
                        content: translation?.content || '',
                    };
                }
            });
            setTranslations(initialTranslations);
        }
    }, [languages, initialData]);

    const loadLanguages = async () => {
        try {
            const { data } = await axios.get('/i18n/languages');
            setLanguages(data);
        } catch (error) {
            console.error('Failed to load languages:', error);
        }
    };

    const loadCategories = async () => {
        try {
            const cats = await eventService.getCategories();
            setCategories(cats);
        } catch (error) {
            console.error('Failed to load categories:', error);
        }
    };

    const handleAddEventDate = () => {
        setEventDates([
            ...eventDates,
            {
                eventDate: '',
                startTime: '',
                endTime: '',
                notes: '',
                sortOrder: eventDates.length,
            },
        ]);
    };

    const handleRemoveEventDate = (index: number) => {
        const newDates = eventDates.filter((_, i) => i !== index);
        // Update sort orders
        newDates.forEach((date, i) => {
            date.sortOrder = i;
        });
        setEventDates(newDates);
    };

    const handleEventDateChange = (
        index: number,
        field: keyof EventDateForm,
        value: string,
    ) => {
        const newDates = [...eventDates];
        newDates[index] = { ...newDates[index], [field]: value };
        setEventDates(newDates);
    };

    const handleTranslationChange = (
        lang: string,
        field: 'title' | 'summary' | 'content',
        value: string,
    ) => {
        setTranslations({
            ...translations,
            [lang]: {
                ...translations[lang],
                [field]: value,
            },
        });
    };

    const handleMarkDuplicate = async (originalPostId: string) => {
        if (!initialData?.id) return;
        setDuplicateLoading(true);
        try {
            const result = await eventService.markAsDuplicate(initialData.id, originalPostId);
            setIsDuplicate(true);
            setDuplicateOf(result?.duplicateOf || null);
            setDuplicateModalOpen(false);
        } catch (error) {
            console.error('Failed to mark as duplicate:', error);
        } finally {
            setDuplicateLoading(false);
        }
    };

    const handleUnmarkDuplicate = async () => {
        if (!initialData?.id) return;
        setDuplicateLoading(true);
        try {
            await eventService.unmarkAsDuplicate(initialData.id);
            setIsDuplicate(false);
            setDuplicateOf(null);
        } catch (error) {
            console.error('Failed to unmark duplicate:', error);
        } finally {
            setDuplicateLoading(false);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const payload: any = {
                type: postType,
                title,
                summary: summary || undefined,
                content: content || undefined,
                keywords: keywords
                    ? keywords.split(',').map((k: string) => k.trim())
                    : undefined,
                categoryId: categoryId || undefined,
                publishedAt,
                isUrgent,
                isActive,
                sourceUrl: sourceUrl || undefined,
                imageUrl: imageUrl || undefined,
            };

            // Only include event-specific fields for events
            if (isEvent) {
                payload.eventLocation = eventLocation || undefined;
                payload.eventDates = eventDates.filter((ed) => ed.eventDate);
            }

            // Add translations if provided - dynamically for all languages
            const translationsPayload: any = {};
            Object.entries(translations).forEach(([langCode, translation]) => {
                if (translation.title) {
                    translationsPayload[langCode] = {
                        title: translation.title,
                        summary: translation.summary || undefined,
                        content: translation.content || undefined,
                    };
                }
            });
            if (Object.keys(translationsPayload).length > 0) {
                payload.translations = translationsPayload;
            }

            await onSubmit(payload);
        } catch (error) {
            console.error('Form submission error:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const categoryOptions = categories.map((cat) => ({
        key: cat.id,
        text: cat.displayName,
        value: cat.id,
    }));

    return (
        <Form loading={loading} className={styles.form}>
            <Header as="h2">
                {isEdit
                    ? `Edit ${contentTypeLabel}`
                    : `Create ${contentTypeLabel}`}
            </Header>

            {/* Duplicate Status Section - Only show when editing */}
            {isEdit && (
                <>
                    <DuplicateModal
                        modalOpen={duplicateModalOpen}
                        post={initialData}
                        postType={postType}
                        onClose={() => setDuplicateModalOpen(false)}
                        onConfirm={handleMarkDuplicate}
                    />
                    <Message
                        color={isDuplicate ? 'orange' : 'green'}
                        style={{ marginBottom: '1rem' }}
                    >
                        <Message.Header>
                            <Icon name={isDuplicate ? 'copy' : 'check circle'} />
                            {isDuplicate ? 'Duplicate Post' : 'Original Post'}
                        </Message.Header>
                        <Message.Content>
                            {isDuplicate && duplicateOf ? (
                                <div style={{ marginTop: '0.5rem' }}>
                                    <p style={{ marginBottom: '0.5rem' }}>
                                        This is a duplicate of:
                                    </p>
                                    <Segment style={{ background: '#f9f9f9' }}>
                                        <Link
                                            to={`/admin/content/edit/${duplicateOf.id}`}
                                            style={{ fontWeight: 'bold', color: '#2185d0' }}
                                        >
                                            {duplicateOf.title}
                                        </Link>
                                        {duplicateOf.summary && (
                                            <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9em' }}>
                                                {duplicateOf.summary}
                                            </p>
                                        )}
                                    </Segment>
                                </div>
                            ) : isDuplicate ? (
                                <p style={{ marginTop: '0.5rem' }}>
                                    This post is marked as a duplicate.
                                </p>
                            ) : (
                                <p style={{ marginTop: '0.5rem' }}>
                                    This post is not marked as a duplicate.
                                </p>
                            )}
                            <div style={{ marginTop: '0.5rem' }}>
                                {isDuplicate ? (
                                    <Button
                                        type="button"
                                        size="small"
                                        loading={duplicateLoading}
                                        onClick={handleUnmarkDuplicate}
                                    >
                                        <Icon name="undo" />
                                        Remove Duplicate Status
                                    </Button>
                                ) : (
                                    <Button
                                        type="button"
                                        size="small"
                                        color="orange"
                                        loading={duplicateLoading}
                                        onClick={() => setDuplicateModalOpen(true)}
                                    >
                                        <Icon name="copy" />
                                        Mark as Duplicate
                                    </Button>
                                )}
                            </div>
                            {/* Plan 001 Phase 2: surface the per-check audit
                                fields so editors can audit the pipeline's
                                decision (verdict, confidence, rationale,
                                full structured comparison). Renders nothing
                                if the row predates audit logging. */}
                            <DedupAuditPanel
                                verdict={initialData?.lastDuplicateCheckVerdict ?? null}
                                confidence={initialData?.lastDuplicateCheckConfidence ?? null}
                                rationale={initialData?.lastDuplicateCheckRationale ?? null}
                                payloadJson={initialData?.lastDuplicateCheckPayload ?? null}
                            />
                        </Message.Content>
                    </Message>
                </>
            )}

            {/* Language Tabs */}
            <Button.Group className={styles.languageTabs}>
                <Button
                    type="button"
                    primary={activeLanguageTab === 'en'}
                    onClick={() => setActiveLanguageTab('en')}
                >
                    English
                </Button>
                {languages
                    .filter((lang) => lang.i18nCode !== 'en')
                    .map((lang) => (
                        <Button
                            key={lang.i18nCode}
                            type="button"
                            primary={activeLanguageTab === lang.i18nCode}
                            onClick={() => setActiveLanguageTab(lang.i18nCode)}
                        >
                            {lang.enName}
                        </Button>
                    ))}
            </Button.Group>

            {/* English Tab */}
            {activeLanguageTab === 'en' && (
                <div>
                    <Form.Field>
                        <label htmlFor="cover-image-url">Cover Image URL</label>
                        <Input
                            id="cover-image-url"
                            value={imageUrl}
                            onChange={(e) => setImageUrl(e.target.value)}
                            placeholder="https://..."
                        />
                        {imageUrl && (
                            <div style={{ marginTop: '15px' }}>
                                <span
                                    style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        fontSize: '14px',
                                        color: '#666',
                                    }}
                                >
                                    Preview:
                                </span>
                                <img
                                    src={imageUrl}
                                    alt="Event cover preview"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '300px',
                                        borderRadius: '8px',
                                        border: '1px solid #e8e8e8',
                                        objectFit: 'contain',
                                    }}
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).style.display =
                                            'none';
                                    }}
                                />
                            </div>
                        )}
                    </Form.Field>

                    <Form.Field>
                        <Checkbox
                            id="event-active"
                            label="Active (uncheck to disable/hide this post)"
                            checked={isActive}
                            onChange={(e, { checked }) =>
                                setIsActive(checked || false)
                            }
                            aria-label="Active (uncheck to disable/hide this post)"
                        />
                    </Form.Field>

                    <Form.Field required>
                        <label htmlFor="event-title">Title</label>
                        <Input
                            id="event-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={`${contentTypeLabel} title`}
                            aria-required="true"
                        />
                    </Form.Field>

                    <Form.Field>
                        <label htmlFor="event-summary">Summary</label>
                        <TextArea
                            id="event-summary"
                            value={summary}
                            onChange={(e) =>
                                setSummary(
                                    (e.target as HTMLTextAreaElement).value,
                                )
                            }
                            placeholder={`Brief summary of the ${contentTypeLabel.toLowerCase()}`}
                            rows={3}
                        />
                    </Form.Field>

                    <Form.Field>
                        <label htmlFor="event-content">Content</label>
                        <TextArea
                            id="event-content"
                            value={content}
                            onChange={(e) =>
                                setContent(
                                    (e.target as HTMLTextAreaElement).value,
                                )
                            }
                            placeholder={`Full ${contentTypeLabel.toLowerCase()} description`}
                            rows={6}
                        />
                    </Form.Field>

                    {isEvent && (
                        <Form.Field required>
                            <label htmlFor="event-location">Event Location</label>
                            <Input
                                id="event-location"
                                value={eventLocation}
                                onChange={(e) =>
                                    setEventLocation(e.target.value)
                                }
                                placeholder="Where the event will take place"
                                aria-required="true"
                            />
                        </Form.Field>
                    )}

                    <Form.Field>
                        <label htmlFor="event-keywords">Keywords (comma-separated)</label>
                        <Input
                            id="event-keywords"
                            value={keywords}
                            onChange={(e) => setKeywords(e.target.value)}
                            placeholder="event, community, city"
                        />
                    </Form.Field>

                    <Form.Field>
                        <label htmlFor="event-category">Category</label>
                        <Dropdown
                            id="event-category"
                            placeholder="Select category"
                            fluid
                            selection
                            options={categoryOptions}
                            value={categoryId}
                            onChange={(e, { value }) =>
                                setCategoryId(value as string)
                            }
                            aria-labelledby="event-category-label"
                        />
                    </Form.Field>

                    <Form.Field required>
                        <label htmlFor="event-published-date">Published Date</label>
                        <Input
                            id="event-published-date"
                            type="date"
                            value={publishedAt}
                            onChange={(e) => setPublishedAt(e.target.value)}
                            aria-required="true"
                        />
                    </Form.Field>

                    <Form.Field>
                        <Checkbox
                            id="event-urgent"
                            label="Mark as urgent"
                            checked={isUrgent}
                            onChange={(e, { checked }) =>
                                setIsUrgent(checked || false)
                            }
                            aria-label="Mark as urgent"
                        />
                    </Form.Field>

                    <Form.Field>
                        <label htmlFor="event-source-url">Source URL</label>
                        <Input
                            id="event-source-url"
                            value={sourceUrl}
                            onChange={(e) => setSourceUrl(e.target.value)}
                            placeholder="https://..."
                        />
                    </Form.Field>

                    {/* Event Dates Section - Only for events */}
                    {isEvent && (
                        <Segment className={styles.eventDatesSection}>
                            <div className={styles.eventDateHeader}>
                                <Header as="h3" style={{ margin: 0 }}>
                                    Event Dates
                                </Header>
                                <Button
                                    type="button"
                                    size="small"
                                    onClick={handleAddEventDate}
                                    primary
                                >
                                    <Icon name="plus" /> Add Date
                                </Button>
                            </div>

                            {eventDates.map((eventDate, index) => (
                                <Segment
                                    key={index}
                                    className={styles.eventDateSegment}
                                >
                                    <div className={styles.eventDateHeader}>
                                        <span>Date #{index + 1}</span>
                                        {eventDates.length > 1 && (
                                            <Button
                                                type="button"
                                                icon
                                                size="mini"
                                                onClick={() =>
                                                    handleRemoveEventDate(index)
                                                }
                                                negative
                                            >
                                                <Icon name="trash" />
                                            </Button>
                                        )}
                                    </div>

                                    <Form.Field required>
                                        <label htmlFor={`event-date-${index}`}>Event Date</label>
                                        <Input
                                            id={`event-date-${index}`}
                                            type="date"
                                            value={eventDate.eventDate}
                                            onChange={(e) =>
                                                handleEventDateChange(
                                                    index,
                                                    'eventDate',
                                                    e.target.value,
                                                )
                                            }
                                            aria-required="true"
                                        />
                                    </Form.Field>

                                    <Form.Group widths="equal">
                                        <Form.Field>
                                            <label htmlFor={`event-start-time-${index}`}>Start Time</label>
                                            <Input
                                                id={`event-start-time-${index}`}
                                                type="time"
                                                value={eventDate.startTime}
                                                onChange={(e) =>
                                                    handleEventDateChange(
                                                        index,
                                                        'startTime',
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                        </Form.Field>

                                        <Form.Field>
                                            <label htmlFor={`event-end-time-${index}`}>End Time</label>
                                            <Input
                                                id={`event-end-time-${index}`}
                                                type="time"
                                                value={eventDate.endTime}
                                                onChange={(e) =>
                                                    handleEventDateChange(
                                                        index,
                                                        'endTime',
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                        </Form.Field>
                                    </Form.Group>

                                    <Form.Field>
                                        <label htmlFor={`event-notes-${index}`}>Notes</label>
                                        <Input
                                            id={`event-notes-${index}`}
                                            value={eventDate.notes}
                                            onChange={(e) =>
                                                handleEventDateChange(
                                                    index,
                                                    'notes',
                                                    e.target.value,
                                                )
                                            }
                                            placeholder="Additional notes for this date"
                                        />
                                    </Form.Field>
                                </Segment>
                            ))}
                        </Segment>
                    )}
                </div>
            )}

            {/* Translation Tabs - Dynamically rendered for all non-English languages */}
            {languages
                .filter((lang) => lang.i18nCode !== 'en')
                .map((lang) => {
                    const langCode = lang.i18nCode;
                    return (
                        activeLanguageTab === langCode && (
                            <div key={langCode}>
                                <Form.Field>
                                    <label htmlFor={`translation-title-${langCode}`}>Title ({lang.enName})</label>
                                    <Input
                                        id={`translation-title-${langCode}`}
                                        value={
                                            translations[langCode]?.title || ''
                                        }
                                        onChange={(e) =>
                                            handleTranslationChange(
                                                langCode,
                                                'title',
                                                e.target.value,
                                            )
                                        }
                                        placeholder={`${contentTypeLabel} title in ${lang.enName}`}
                                    />
                                </Form.Field>

                                <Form.Field>
                                    <label htmlFor={`translation-summary-${langCode}`}>Summary ({lang.enName})</label>
                                    <TextArea
                                        id={`translation-summary-${langCode}`}
                                        value={
                                            translations[langCode]?.summary ||
                                            ''
                                        }
                                        onChange={(e) =>
                                            handleTranslationChange(
                                                langCode,
                                                'summary',
                                                (
                                                    e.target as HTMLTextAreaElement
                                                ).value,
                                            )
                                        }
                                        placeholder={`Brief summary in ${lang.enName}`}
                                        rows={3}
                                    />
                                </Form.Field>

                                <Form.Field>
                                    <label htmlFor={`translation-content-${langCode}`}>Content ({lang.enName})</label>
                                    <TextArea
                                        id={`translation-content-${langCode}`}
                                        value={
                                            translations[langCode]?.content ||
                                            ''
                                        }
                                        onChange={(e) =>
                                            handleTranslationChange(
                                                langCode,
                                                'content',
                                                (
                                                    e.target as HTMLTextAreaElement
                                                ).value,
                                            )
                                        }
                                        placeholder={`Full description in ${lang.enName}`}
                                        rows={6}
                                    />
                                </Form.Field>
                            </div>
                        )
                    );
                })}

            {/* Form Actions */}
            <div className={styles.formActions}>
                <Button type="button" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    type="submit"
                    primary
                    onClick={handleSubmit}
                    loading={loading}
                >
                    {isEdit
                        ? `Update ${contentTypeLabel}`
                        : `Create ${contentTypeLabel}`}
                </Button>
            </div>
        </Form>
    );
};
